import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../assets/css/App.css";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
// Ensure no double slashes in URL construction
const API_URL = `${API_BASE.replace(/\/$/, '')}/auth`;

const App = () => {
  const [name, setName] = useState("");       // For signup name input
  const [email, setEmail] = useState("");     // For email input
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!forgotPasswordEmail) {
      setMessage("❌ Please enter your email address");
      return;
    }

    try {
      const forgotPasswordUrl = `${API_URL}/forgot-password`;
      
      const res = await fetch(forgotPasswordUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotPasswordEmail }),
        credentials: "include",
      });
      

      // Handle 404 specifically
      if (res.status === 404) {
        throw new Error(`Route not found. Please check if the backend is running and the endpoint ${forgotPasswordUrl} exists.`);
      }

      // Check if response is JSON before parsing
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error("Non-JSON response received:", text.substring(0, 200));
        throw new Error("Server returned an invalid response. Please try again later.");
      }

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || "Failed to send reset email");
      }

      setMessage(`✅ ${data.message || "Password reset email sent! Please check your inbox."}`);
      
      setTimeout(() => {
        setIsForgotPassword(false);
        setForgotPasswordEmail("");
        setMessage("");
      }, 3000);
    } catch (err) {
      console.error("Forgot password error:", err);
      if (err.message.includes("JSON")) {
        setMessage("❌ Server error. Please try again later.");
      } else {
        setMessage(`❌ ${err.message}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      if (isSignup) {
        const minLengthOk = password.length >= 15;
        const minUppercaseOk = /[A-Z]/.test(password);
        const minSymbolsOk = /[^A-Za-z0-9]/.test(password);

        if (!minLengthOk || !minUppercaseOk || !minSymbolsOk) {
          setMessage("❌ Password must be at least 15 characters, include 1 uppercase letter and 1 symbol.");
          return;
        }

        // === REGISTER ===
        const res = await fetch(`${API_URL}/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            email,
            password,
          }),
          credentials: "include",
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Registration failed");

        setMessage(`✅ Welcome, ${data.name}! Redirecting to Sign In...`);

        setTimeout(() => {
          setIsSignup(false);
          setName("");
          setEmail("");
          setPassword("");
          navigate("/login");
        }, 1500);
      } else {
        // === LOGIN ===
        const res = await fetch(`${API_URL}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
          }),
          credentials: "include",
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Login failed");

        setMessage(`✅ Logged in as ${data.name}`);

        // Save login info to localStorage
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("user", JSON.stringify(data)); // Assuming data contains user info directly
        localStorage.setItem("token", data.token);

        // Expire old pending payments (older than 5 minutes)
        try {
          await fetch(`${API_BASE.replace(/\/$/, '')}/courses/pay/expire-old-pending`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${data.token}`,
            },
          });
        } catch (err) {
          console.error("Error expiring old pending payments:", err);
          // Don't block login if this fails
        }

        setTimeout(() => {
          navigate("/Home-V1=");
        }, 1000);
      }
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    }
  };

  return (
    <>
      {/* === Animated Background === */}
      <section className="background">
        {Array(130)
          .fill("")
          .map((_, index) => (
            <span key={index}></span>
          ))}
      </section>

      {/* === Centered Signin Area === */}
      <div className="signin-wrapper">
        <div className="signin">
          <div className="content">
            {isForgotPassword ? (
              <>
                <h2>Forgot Password</h2>
                <form className="form" onSubmit={handleForgotPassword}>
                  <div className="inputBox">
                    <input
                      type="email"
                      required
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="Email"
                    />
                    <i>Email</i>
                  </div>

                  <div className="links">
                    <button
                      type="button"
                      onClick={() => {
                        setIsForgotPassword(false);
                        setForgotPasswordEmail("");
                        setMessage("");
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "inherit",
                        cursor: "pointer",
                        textDecoration: "underline",
                        padding: 0,
                      }}
                    >
                      Back to Sign In
                    </button>
                  </div>

                  <div className="inputBox">
                    <input type="submit" value="Send Reset Link" />
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2>{isSignup ? "Sign Up" : "Sign In"}</h2>
                <form className="form" onSubmit={handleSubmit}>
                  {isSignup && (
                    <div className="inputBox">
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Name"
                      />
                      <i>Name</i>
                    </div>
                  )}

                  <div className="inputBox">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email"
                    />
                    <i>Email</i>
                  </div>

                  <div className="inputBox" style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      style={{ paddingRight: "3rem" }}
                    />
                    <i>Password</i>
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "inherit",
                        cursor: "pointer",
                        fontSize: "2rem",
                        padding: 0,
                      }}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>

                  {isSignup && (
                      <div style={{  fontSize: "0.95em" }}>
    Select the required password parameters:
  
                    <div style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "8px", color: "#fff", fontSize: "0.95em" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <strong style={{ color: password.length >= 15 ? "#4caf50" : "#ff6b6b" }}>{password.length >= 15 ? "✅" : "❌"}</strong>
                        <span style={{ opacity: 0.95 }}>15 chars</span>
                      </span>

                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <strong style={{ color: /[A-Z]/.test(password) ? "#4caf50" : "#ff6b6b" }}>{/[A-Z]/.test(password) ? "✅" : "❌"}</strong>
                        <span style={{ opacity: 0.95 }}>1 uppercase</span>
                      </span>

                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <strong style={{ color: /[^A-Za-z0-9]/.test(password) ? "#4caf50" : "#ff6b6b" }}>{/[^A-Za-z0-9]/.test(password) ? "✅" : "❌"}</strong>
                        <span style={{ opacity: 0.95 }}>1 symbol</span>
                      </span>
                    </div>
                    </div>
                  )}

                  <div className="links">
                    {!isSignup && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotPassword(true);
                          setMessage("");
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "inherit",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: 0,
                        }}
                      >
                        Forgot Password?
                      </button>
                    )}

                    {isSignup ? (
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsSignup(false);
                          setMessage("");
                        }}
                      >
                        Already have an account? Sign In
                      </Link>
                    ) : (
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsSignup(true);
                          setMessage("");
                        }}
                      >
                        Don't have an account? 
                        <br />
                        <em className="fw-bold">Sign Up</em>
                      </Link>
                    )}
                  </div>

                  <div className="inputBox">
                    <input type="submit" value={isSignup ? "Sign Up" : "Login"} />
                  </div>
                </form>
              </>
            )}

            {message && (
              <p style={{ color: "#fff", marginTop: "10px", textAlign: "center" }}>{message}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default App;
